import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Copy, FileText, Loader2, AlertCircle, Info } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";

// Laporan duplikat — READ ONLY. Tidak ada aksi hapus/arsip di sini; menandai
// untuk ditinjau admin sesuai keputusan "laporan saja" (MoM Action Item #5).

interface DupDoc {
  id: string;
  judul: string | null;
  nomor_dokumen: string | null;
  file_name: string | null;
  status: string | null;
  created_at: string | null;
  ai_processed: boolean;
}

interface DupGroup {
  type: "exact" | "near";
  score: number;
  key: string | null;
  documents: DupDoc[];
}

interface DupReport {
  groups: DupGroup[];
  summary: {
    total_documents: number;
    exact_groups: number;
    near_groups: number;
    documents_affected: number;
    documents_not_indexed: number;
    near_threshold: number;
  };
}

function StatCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export default function DuplicatesPage() {
  const { data, isLoading, isError } = useQuery<DupReport>({
    queryKey: ["document-duplicates"],
    queryFn: () => api.get("/documents/duplicates"),
  });

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2">
          <Copy className="h-5 w-5 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Deteksi Duplikat Dokumen</h1>
        </div>
        <p className="mt-0.5 text-sm text-gray-500">
          Laporan grup dokumen duplikat berdasarkan isi (exact-hash + near-duplicate).
          Bersifat tinjauan — tidak ada penghapusan otomatis.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Memindai dokumen…
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> Gagal memuat laporan duplikat.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label="Dokumen Dipindai" value={data.summary.total_documents} />
            <StatCard label="Grup Exact" value={data.summary.exact_groups} hint="file identik" />
            <StatCard label="Grup Near" value={data.summary.near_groups} hint="isi mirip" />
            <StatCard label="Dok Terdampak" value={data.summary.documents_affected} />
            <StatCard
              label="Belum Terindeks"
              value={data.summary.documents_not_indexed}
              hint="near-dup dilewati"
            />
          </div>

          {data.summary.documents_not_indexed > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {data.summary.documents_not_indexed} dokumen belum terindeks (RAG) sehingga
              near-duplicate berbasis isi tidak dapat dihitung untuk dokumen tersebut.
              Exact-duplicate tetap terdeteksi.
            </div>
          )}

          {data.groups.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-400">
              Tidak ditemukan duplikat.
            </div>
          ) : (
            <div className="space-y-4">
              {data.groups.map((g, i) => (
                <div key={i} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/60 px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-xs font-semibold",
                          g.type === "exact"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-violet-100 text-violet-700",
                        )}
                      >
                        {g.type === "exact" ? "EXACT (file identik)" : "NEAR (isi mirip)"}
                      </span>
                      <span className="text-xs text-gray-500">
                        skor {g.score.toFixed(4)} · {g.documents.length} dokumen
                      </span>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      {g.documents.map((d) => (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" />
                              <div className="min-w-0">
                                <Link
                                  to={`/documents/${d.id}`}
                                  className="font-medium text-blue-600 hover:underline line-clamp-1"
                                >
                                  {d.judul || d.file_name || d.id}
                                </Link>
                                <p className="text-xs text-gray-400">
                                  {(d.nomor_dokumen || "tanpa nomor")} ·{" "}
                                  {d.status || "—"} ·{" "}
                                  {d.created_at
                                    ? new Date(d.created_at).toLocaleDateString("id-ID")
                                    : "—"}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
